import { styled } from '@mui/material';

import { ReactNode } from 'react';

import { I18nResources, useI18n } from '@app-velocity/i18n';
import { usePreviewHighlight } from '@app-velocity/common';

import {
  ErrorCircleIcon,
  InfoCircleIcon,
  NavigateIcon,
  WarningCircleIcon,
} from '@app-velocity/icons';

import { TAppraisalDto } from '@app-velocity/av-api-v2';

import { useAppraisalContext } from '../../../../context/useAppraisalContext';

export type AppraisalElementProps = {
  review: TAppraisalDto;
};

const StyledAppraisalElement = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  width: '100%',
  cursor: 'pointer',
}));

const AppraisalElementHeader = styled('header')<{
  level: TAppraisalDto['level'];
}>(({ theme, level }) => ({
  display: 'flex',
  aligItems: 'center',
  justifyContent: 'space-between',
  color: getColorByLevel(level),
  fontSize: 13,
  fontWeight: 600,
  gap: theme.spacing(1),
}));

const Title = styled('div')({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: 1,
});

const Remark = styled('p')(({ theme }) => ({
  margin: 0,
  marginBottom: theme.spacing(1),
  fontSize: 11,
  color: 'var(--text-text-secondary-color)',
  fontWeight: 300,
}));

const Review = styled('li')({
  width: '100%',
  margin: 0,
});

const Buttons = styled('div')(({ theme }) => ({
  gap: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
}));

const Section = styled('div')(({ theme }) => ({
  minWidth: 0,
  gap: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
}));

const Level = styled('div')(({ theme }) => ({
  minWidth: 0,
  flex: '0 0 auto',
  gap: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
}));

const IconContainer = styled('div')<{ level: TAppraisalDto['level'] }>(
  ({ theme, level }) => ({
    gap: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    svg: {
      fill: getIconColorByLevel(level),
    },
  })
);

const NavigateButton = styled('button')(({ theme }) => ({
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: `0 ${theme.spacing(1)}`,
  svg: {
    fill: 'var(--icon-icon-secondary-color)',
  },
}));

const getIconColorByLevel = (level: TAppraisalDto['level']) => {
  switch (level) {
    case 'ERROR':
      return 'var(--error-error-icon-primary-color)';
    case 'WARNING':
      return 'var(--warning-warning-icon-primary-color)';
    case 'INFO':
      return 'var(--info-info-icon-primary-color)';
  }
};

const getColorByLevel = (level: TAppraisalDto['level']) => {
  switch (level) {
    case 'ERROR':
      return '#931616';
    case 'WARNING':
      return '#B58015';
    case 'INFO':
      return '#1747A4';
  }
};

const getLevel = (level: TAppraisalDto['level']): I18nResources => {
  switch (level) {
    case 'ERROR':
      return 'appraisals:level.error';
    case 'WARNING':
      return 'appraisals:level.warning';
    case 'INFO':
      return 'appraisals:level.notice';
  }
};

const getIcon = (level: TAppraisalDto['level']): ReactNode => {
  switch (level) {
    case 'ERROR':
      return <ErrorCircleIcon />;
    case 'WARNING':
      return <WarningCircleIcon />;
    case 'INFO':
      return <InfoCircleIcon />;
  }
};

export const AppraisalElement = ({ review }: AppraisalElementProps) => {
  const {
    navigateToElement,
    nodeName,
    nodeId,
    elementLabelSource = 'name',
  } = useAppraisalContext();
  const { setHighlight } = usePreviewHighlight();
  const { t } = useI18n();

  const getSanitizedText = (name: string) => {
    const surrogatesRangeStart = 55296;
    const surrogatesRangeEnd = 57343;

    const filtered = [...name].filter(
      (char) =>
        char.charCodeAt(0) < surrogatesRangeStart ||
        char.charCodeAt(0) > surrogatesRangeEnd
    );

    return filtered.length > 0
      ? filtered.join('')
      : t('appraisals:unable_to_render_text');
  };

  const getElementLabel = () => {
    if (elementLabelSource === 'text') {
      if (review.elementText) {
        return getSanitizedText(review.elementText);
      }
    }

    return getSanitizedText(
      review.elementName || nodeName?.split('--||--')[0] || ''
    );
  };

  const handleNavigateToElement = () => {
    if (!navigateToElement || (!review.elementNodeId && !nodeId)) return;

    navigateToElement(review.elementNodeId || nodeId);
  };

  const handleMouseEnter = () => {
    const { boundingBox } = review;
    if (!boundingBox) return;

    const { x, y, width, height } = boundingBox;

    if (x == null || y == null || width == null || height == null) {
      return;
    }

    setHighlight({
      rect: {
        x,
        y,
        width,
        height,
      },
      level: review.level,
    });
  };

  const handleMouseLeave = () => {
    setHighlight(null);
  };

  return (
    <StyledAppraisalElement
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <AppraisalElementHeader
        level={review.level}
        onClick={handleNavigateToElement}
      >
        <Section>
          <Title>{getElementLabel()}</Title>
          <Buttons>
            {navigateToElement ? (
              <NavigateButton>
                <NavigateIcon />
              </NavigateButton>
            ) : null}
          </Buttons>
        </Section>
        <Level>
          {t(getLevel(review.level))}
          <IconContainer level={review.level}>
            {getIcon(review.level)}
          </IconContainer>
        </Level>
      </AppraisalElementHeader>
      <Review>
        {review.description ? <Remark>{review.description}</Remark> : null}
      </Review>
    </StyledAppraisalElement>
  );
};
